import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { saveSystemConfig } from "../api";
import { effectiveColorScale } from "../colorScaleDisplay";
import { fetchLabels, type LidarClearanceLabel } from "../labelsApi";
import { regionKey, useLidarClearance } from "../LidarClearanceContext";
import type { LidarClearanceColorScale, LidarClearanceSystem } from "../schemas";
import SystemPicker from "../SystemPicker";
import t from "../translations";
import { labelNamesByAxis } from "./axisValueLabels";
import ConfirmSkippedEntriesDialog from "./ConfirmSkippedEntriesDialog";
import CopyRulesConfigDialog from "./CopyRulesConfigDialog";
import EntityCheckCard from "./EntityCheckCard";
import ParametersSection from "./ParametersSection";
import RuleCard from "./RuleCard";
import SectionHeader from "./SectionHeader";
import {
  buildClearanceConfig,
  clearParameterReferences,
  duplicateWizardRule,
  newWizardEntityCheck,
  newWizardRule,
  removeByKey,
  renameParameterReferences,
  updateByKey,
  type WizardEntityCheck,
  type WizardParameter,
  type WizardRule,
} from "./clearanceRulesConfig";
import {
  duplicateActionRuleKeys,
  duplicateConditionRuleKeys,
  duplicateParameterNames,
} from "./ruleDuplicateChecks";
import { parseStoredConfig } from "./storedConfig";

export default function RulesTab() {
  const { regionTags, systems, systemAxisLinksBySystem, colorScaleByKey, lidarTypes, refetch } =
    useLidarClearance();
  const [region, setRegion] = useState("");
  const [systemId, setSystemId] = useState<number | null>(null);
  const [rules, setRules] = useState<WizardRule[]>([]);
  const [entityChecks, setEntityChecks] = useState<WizardEntityCheck[]>([]);
  const [parameters, setParameters] = useState<WizardParameter[]>([]);
  const [expandedRuleKeys, setExpandedRuleKeys] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [labels, setLabels] = useState<LidarClearanceLabel[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [confirmSkippedOpen, setConfirmSkippedOpen] = useState(false);

  // regionTags starts empty while LidarClearanceProvider's fetch is in
  // flight (e.g. a direct `?tab=rules` load), so the initial `region` state
  // above can't default to a real tag yet - pick one as soon as the data
  // arrives, but only while nothing's been chosen, so it doesn't clobber the
  // user's own selection on a later refetch.
  useEffect(() => {
    if (region === "" && regionTags.length > 0) {
      setRegion(regionTags[0]);
    }
  }, [region, regionTags]);

  const selectedSystem =
    systems.find((system) => system.id === systemId && system.region === region) ?? null;

  const links = selectedSystem
    ? systemAxisLinksBySystem.get(
        regionKey(selectedSystem.region, selectedSystem.id)
      ) ?? []
    : [];
  const axes = useMemo(() => links.map((entry) => entry.axis), [links]);
  const labelNames = useMemo(() => labelNamesByAxis(labels), [labels]);
  const axisColorScales = useMemo(() => {
    const map = new Map<number, LidarClearanceColorScale>();
    for (const entry of links) {
      const scale = effectiveColorScale(colorScaleByKey, entry.axis.region, entry);
      if (scale) map.set(entry.axis.id, scale);
    }
    return map;
  }, [links, colorScaleByKey]);
  const regionLidarTypes = useMemo(
    () => lidarTypes.filter((lidarType) => lidarType.region === region),
    [lidarTypes, region]
  );

  useEffect(() => {
    if (!selectedSystem) {
      setLabels([]);
      return;
    }
    let cancelled = false;
    // Axis ids are region-scoped, so a stale label from the previous system
    // can be mistaken for the new one's while this fetch is in flight (or
    // left showing indefinitely if it fails) - clear it up front.
    setLabels([]);
    fetchLabels(selectedSystem.region, selectedSystem.id)
      .then((fetched) => {
        if (!cancelled) setLabels(fetched);
      })
      .catch((error) => {
        captureException(error, {
          tags: { feature: "lidarClearance.fetchLabels" },
        });
        toast.error(t.FailedToLoadLabels);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSystem]);

  const loadFromConfig = (config: Record<string, unknown> | null | undefined) => {
    const parsed = parseStoredConfig(config);
    setRules(parsed.rules);
    setEntityChecks(parsed.entityChecks);
    setParameters(parsed.parameters);
    // Rules keep fresh keys each parse, so a loaded config's rules never
    // match a stale expanded set - explicit reset just avoids that set
    // growing unbounded with keys from systems no longer selected.
    setExpandedRuleKeys(new Set());
    setSkippedCount(parsed.skippedCount);
    if (parsed.skippedCount > 0) {
      toast.warning(t.SkippedInvalidConfigEntries(parsed.skippedCount));
    }
  };

  const selectSystem = (id: number | null) => {
    setSystemId(id);
    const system = systems.find((candidate) => candidate.id === id && candidate.region === region);
    loadFromConfig(system?.config);
  };

  const handleCopyFrom = (source: LidarClearanceSystem) => loadFromConfig(source.config);

  const addRule = () => {
    const rule = newWizardRule();
    setRules((prev) => [...prev, rule]);
    setExpandedRuleKeys((prev) => new Set(prev).add(rule.key));
  };

  const updateRule = (key: string, updated: WizardRule) =>
    setRules((prev) => updateByKey(prev, key, updated));

  const removeRule = (key: string) =>
    setRules((prev) => removeByKey(prev, key));

  const duplicateRule = (key: string) => {
    const index = rules.findIndex((rule) => rule.key === key);
    if (index === -1) return;
    const duplicate = duplicateWizardRule(rules[index]);
    setRules((prev) => [...prev.slice(0, index + 1), duplicate, ...prev.slice(index + 1)]);
    setExpandedRuleKeys((prev) => new Set(prev).add(duplicate.key));
  };

  const moveRule = (key: string, direction: -1 | 1) =>
    setRules((prev) => {
      const index = prev.findIndex((rule) => rule.key === key);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const toggleRuleExpanded = (key: string) =>
    setExpandedRuleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const expandAllRules = () => setExpandedRuleKeys(new Set(rules.map((rule) => rule.key)));
  const collapseAllRules = () => setExpandedRuleKeys(new Set());

  const addEntityCheck = () =>
    setEntityChecks((prev) => [...prev, newWizardEntityCheck()]);

  const updateEntityCheck = (key: string, updated: WizardEntityCheck) =>
    setEntityChecks((prev) => updateByKey(prev, key, updated));

  const removeEntityCheck = (key: string) =>
    setEntityChecks((prev) => removeByKey(prev, key));

  // A rule/action's WizardValue can reference a parameter by name - renaming
  // or deleting one here would otherwise leave those references pointing at
  // a name that's no longer declared (silently invalid on next save), so
  // carry the same change through every rule that used it.
  const handleParametersChange = (next: WizardParameter[]) => {
    let nextRules = rules;
    for (const prev of parameters) {
      if (prev.name === "") continue;
      const updated = next.find((candidate) => candidate.key === prev.key);
      if (!updated) nextRules = clearParameterReferences(nextRules, prev.name);
      else if (updated.name !== "" && updated.name !== prev.name)
        nextRules = renameParameterReferences(nextRules, prev.name, updated.name);
    }
    if (nextRules !== rules) setRules(nextRules);

    setParameters(next);
  };

  const hasDuplicateParameterNames = useMemo(
    () => duplicateParameterNames(parameters).size > 0,
    [parameters]
  );

  const duplicateConditionKeys = useMemo(
    () => duplicateConditionRuleKeys(rules, parameters),
    [rules, parameters]
  );
  const duplicateActionKeys = useMemo(
    () => duplicateActionRuleKeys(rules, parameters),
    [rules, parameters]
  );

  const config = useMemo(
    () =>
      buildClearanceConfig(
        axes.map((axis) => ({ name: axis.name_en, max_value: axis.max_value })),
        rules,
        entityChecks,
        parameters
      ),
    [axes, rules, entityChecks, parameters]
  );
  const json = useMemo(() => JSON.stringify(config, null, 2), [config]);

  const performSave = async () => {
    if (!selectedSystem) return;
    setSaving(true);
    try {
      await saveSystemConfig(selectedSystem.region, selectedSystem.id, config);
      await refetch();
      toast.success(t.SavedConfigMessage);
      setSkippedCount(0);
    } catch (error) {
      captureException(error, {
        tags: { feature: "lidarClearance.saveSystemConfig" },
      });
      toast.error(t.FailedToSaveConfig);
    } finally {
      setSaving(false);
    }
  };

  // Saving serializes only the wizard's own state, which parseStoredConfig
  // already dropped any unreadable entries from - going straight to
  // performSave here would silently and permanently delete them from the
  // stored config, so make that loss an explicit choice instead.
  const handleSave = () => {
    if (skippedCount > 0) {
      setConfirmSkippedOpen(true);
      return;
    }
    performSave();
  };

  const handleConfirmSkippedSave = async () => {
    setConfirmSkippedOpen(false);
    await performSave();
  };

  return (
    <Box data-testid="rules-tab">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t.RulesTabIntro}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: "center" }}>
        <TextField
          select
          label={t.RegionLabel}
          value={region}
          onChange={(event) => {
            setRegion(event.target.value);
            selectSystem(null);
          }}
          sx={{ width: 160 }}
        >
          {regionTags.map((tag) => (
            <MenuItem key={tag} value={tag}>
              {tag}
            </MenuItem>
          ))}
        </TextField>
        <SystemPicker region={region} value={systemId} onChange={selectSystem} />

        {selectedSystem && (
          <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
            {axes.length > 0 && (
              <Tooltip title={t.SaveConfigButton}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || hasDuplicateParameterNames}
                    data-testid="rules-tab.save"
                  >
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={t.CopyFromSystemButton}>
              <IconButton
                onClick={() => setCopyDialogOpen(true)}
                aria-label={t.CopyFromSystemButton}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {selectedSystem && (
        <>
          {axes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t.NoLinkedAxesHint}
            </Typography>
          ) : (
            <>
              <SectionHeader title={t.ParametersTitle} hint={t.ParametersHint} />
              <ParametersSection parameters={parameters} onChange={handleParametersChange} />
              {hasDuplicateParameterNames && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {t.DuplicateParameterNamesBlockSave}
                </Typography>
              )}

              <SectionHeader title={t.RulesTab} hint={t.RulesSectionDescription} sx={{ mt: 2 }} />
              {rules.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button size="small" onClick={expandAllRules}>
                    {t.ExpandAllButton}
                  </Button>
                  <Button size="small" onClick={collapseAllRules}>
                    {t.CollapseAllButton}
                  </Button>
                </Stack>
              )}
              {rules.map((rule, index) => (
                <RuleCard
                  key={rule.key}
                  rule={rule}
                  axes={axes}
                  labelNames={labelNames}
                  axisColorScales={axisColorScales}
                  lidarTypes={regionLidarTypes}
                  parameters={parameters}
                  hasDuplicateConditions={duplicateConditionKeys.has(rule.key)}
                  hasDuplicateActions={duplicateActionKeys.has(rule.key)}
                  expanded={expandedRuleKeys.has(rule.key)}
                  isFirst={index === 0}
                  isLast={index === rules.length - 1}
                  onToggleExpanded={() => toggleRuleExpanded(rule.key)}
                  onChange={(updated) => updateRule(rule.key, updated)}
                  onRemove={() => removeRule(rule.key)}
                  onDuplicate={() => duplicateRule(rule.key)}
                  onMoveUp={() => moveRule(rule.key, -1)}
                  onMoveDown={() => moveRule(rule.key, 1)}
                />
              ))}
              <Button startIcon={<AddIcon />} onClick={addRule} sx={{ mb: 3 }}>
                {t.AddRuleButton}
              </Button>

              <SectionHeader title={t.EntityChecksTitle} hint={t.EntityChecksHint} sx={{ mt: 2 }} />
              {entityChecks.map((check) => (
                <EntityCheckCard
                  key={check.key}
                  check={check}
                  axes={axes}
                  labelNames={labelNames}
                  axisColorScales={axisColorScales}
                  onChange={(updated) => updateEntityCheck(check.key, updated)}
                  onRemove={() => removeEntityCheck(check.key)}
                />
              ))}
              <Button startIcon={<AddIcon />} onClick={addEntityCheck} sx={{ mb: 3 }}>
                {t.AddEntityCheckButton}
              </Button>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">{t.JsonPreviewTitle}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      overflow: "auto",
                      maxHeight: 320,
                      fontSize: 12,
                    }}
                    data-testid="rules-tab.json-preview"
                  >
                    {json}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}

          <CopyRulesConfigDialog
            open={copyDialogOpen}
            region={selectedSystem.region}
            systemId={selectedSystem.id}
            onClose={() => setCopyDialogOpen(false)}
            onCopy={handleCopyFrom}
          />

          <ConfirmSkippedEntriesDialog
            open={confirmSkippedOpen}
            skippedCount={skippedCount}
            submitting={saving}
            onCancel={() => setConfirmSkippedOpen(false)}
            onConfirm={handleConfirmSkippedSave}
          />
        </>
      )}
    </Box>
  );
}
